import { datadogRum, type PropagatorType } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'
import { reactPlugin } from '@datadog/browser-rum-react'
import config from 'constants/config'
import { sanitizeErrorMessage, sanitizeForLogging } from '../lib/utils/redactPii'
import { reportClientError } from './errorReportingService'

// Frontend RUM service. The backend APM service is 'www-le-business-traveller-main'
// (see service.datadog.yaml). RUM-to-APM linking uses trace propagation headers,
// not service name matching.
const DD_SERVICE = 'www-le-business-traveller'

// The SDK's own guard emits a console.error on duplicate init(), which
// forwardConsoleLogs would forward to Datadog Logs as noise.
let initialized = false

export function initDatadogRum() {
  if (initialized) return
  initialized = true

  const applicationId = config.DATADOG_RUM_APPLICATION_ID
  const clientToken = config.DATADOG_RUM_CLIENT_TOKEN

  if (!applicationId || !clientToken) {
    return
  }

  const env = config.ENV ?? 'unknown'
  const commitSha = process.env.COMMIT_SHA || undefined
  const version = commitSha ? commitSha.substring(0, 7) : 'unknown'

  const tracePropagationEnabled = config.DATADOG_RUM_TRACE_PROPAGATION_ENABLED === true
  const allowedTracingUrls = tracePropagationEnabled
    ? [
        {
          match: /^https:\/\/(api\.lebusinesstraveller\.com|test-api\.luxuryescapes\.com|api\.test\.luxuryescapes\.com|cdn\.test\.luxuryescapes\.com)/,
          propagatorTypes: ['datadog', 'tracecontext'] as Array<PropagatorType>,
        },
      ]
    : []

  datadogRum.init({
    applicationId,
    clientToken,
    site: config.DATADOG_RUM_SITE ?? 'ap2.datadoghq.com',
    service: DD_SERVICE,
    env,
    version,
    sessionSampleRate: 100,
    // 5% replay to control cost. defaultPrivacyLevel masks input fields only;
    // PII displayed in non-input elements (e.g. confirmation pages) may still
    // be captured — confirm with privacy/compliance before increasing the rate.
    sessionReplaySampleRate: 0,
    defaultPrivacyLevel: 'mask-user-input',
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    // Controlled by REACT_APP_DATADOG_RUM_TRACE_PROPAGATION_ENABLED. When true, injects trace
    // headers on API calls. When false/unset, disabled (avoids CORS preflight until backend adds
    // X-Datadog-*, traceparent, tracestate to Access-Control-Allow-Headers).
    allowedTracingUrls,
    // @datadog/browser-rum-react v6.x router integration requires react-router v6 or v7.
    // This project uses react-router v5.3.4, so router: true would silently fail to track
    // route changes. Set to false until react-router is upgraded to v6+.
    plugins: [reactPlugin({ router: false })],
  })

  datadogLogs.init({
    clientToken,
    site: config.DATADOG_RUM_SITE ?? 'ap2.datadoghq.com',
    service: DD_SERVICE,
    env,
    version,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['error', 'warn'],
    sessionSampleRate: 100,
  })
}

export function setDatadogUser(userId: string | number | undefined | null) {
  if (typeof window === 'undefined') return
  try {
    if (userId != null && userId !== '') {
      const user = { id: String(userId) }
      datadogRum.setUser(user)
      datadogLogs.setUser(user)
    } else {
      datadogRum.clearUser()
      datadogLogs.clearUser()
    }
  } catch {
    // Silently fail
  }
}

export function logDatadog(message: string, data: any, level: 'info' | 'warn' | 'error' = 'error') {
  if (typeof window === 'undefined') return
  try {
    const sanitized = data != null && typeof data === 'object' ? sanitizeForLogging(data) : data
    const context = sanitized != null && typeof sanitized === 'object' ? sanitized : { value: sanitized }
    const sanitizedMessage = sanitizeErrorMessage(message)

    if (level === 'error') {
      datadogRum.addError(new Error(sanitizedMessage), context)
      datadogLogs.logger.error(sanitizedMessage, context)
    } else if (level === 'warn') {
      datadogRum.addAction(sanitizedMessage, context)
      datadogLogs.logger.warn(sanitizedMessage, context)
    } else {
      datadogRum.addAction(sanitizedMessage, context)
      datadogLogs.logger.info(sanitizedMessage, context)
    }
  } catch {
    try {
      reportClientError(new Error('logDatadog: Datadog logging failed'), {
        originalMessage: sanitizeErrorMessage(message),
        attemptedLevel: level,
      })
    } catch {
      // Both Datadog and Sentry failed; don't crash the app
    }
  }
}

/**
 * Creates a logger function with predefined context that can be reused throughout a component/file
 * @param baseContext - Common context data to be included in all logs (e.g., businessId, userId, etc.)
 * @returns Logger function that accepts message, level, and additional context
 */
export function createDatadogLoggerWithContext(baseContext: Record<string, any>) {
  return (message: string, level: 'info' | 'warn' | 'error', additionalContext: Record<string, any> = {}) => {
    logDatadog(message, { ...baseContext, ...additionalContext }, level)
  }
}
